import * as React from 'react';
import { graphql } from 'react-apollo';
import { Helmet } from 'react-helmet';

import gql from 'graphql-tag';
import URI from 'jsuri';
import { compose, withProps } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import { PaginationControls, Row, color, font } from '@coursera/coursera-ui';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import { ReviewFragment } from 'bundles/reviews-common/components/Review';
import type { ProductReview, ProductReviewsGraphqlArgs } from 'bundles/reviews-common/types/reviewsSharedTypes';
import ReviewsFilters from 'bundles/reviews/components/ReviewsFilters';
import ReviewsList from 'bundles/reviews/components/ReviewsList';
import ReviewsSort from 'bundles/reviews/components/ReviewsSort';
import type {
  AllCourseReviews,
  AllCourseReviewsVariables,
} from 'bundles/reviews/components/__generated__/AllCourseReviews';
import { REVIEWS_PER_PAGE } from 'bundles/reviews/constants';
import type { QueryParams, ReviewRouter } from 'bundles/reviews/types/reviewTypes';
import { parseStarQueryParam } from 'bundles/reviews/utils/utils';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

type InputProps = {
  courseName: string;
  courseId: string;
};

type Props = InputProps & {
  paging?: {
    total: number;
  };
  courseName: string;
  currentPage: number;
  reviews?: Array<ProductReview>;
  loading?: boolean;
  productCompleted?: boolean;
  ratingValues?: Array<number>;
  sortByHelpfulVotes: boolean;
  router: ReviewRouter;
};

type State = {
  learnerFilterLabel: string;
  learnerFilterValue?: boolean;
  sortByValue: boolean;
  sortByLabel: string;
};

const styles: { [key: string]: React.CSSProperties } = {
  numberButtonStyle: {
    marginLeft: '6px',
    marginRight: '6px',
    padding: '10px',
    width: '36px',
    height: '36px',
    fontSize: font.sm,
    lineHeight: '1.6rem',
    color: `${color.secondaryText} !important`,
  },
  arrowButtonStyle: {
    marginLeft: '2px',
    marginRight: '2px',
    padding: '10px',
    paddingTop: '14px',
    width: '37px',
    height: '49px',
    fontSize: font.sm,
    color: color.white,
    backgroundColor: '#0056D2',
    borderRadius: '2px',
  },
  disabledArrowButtonStyle: {
    marginLeft: '2px',
    marginRight: '2px',
    padding: '10px',
    paddingTop: '14px',
    width: '37px',
    height: '49px',
    fontSize: font.sm,
    color: color.primaryText,
    backgroundColor: '#E5E7E8',
    borderColor: '#E5E7E8',
    borderRadius: '2px',
  },
};

export class ReviewsContainer extends React.Component<Props, State> {
  renderCanonicalTags = () => {
    const {
      router: {
        location,
        location: { query },
      },
      currentPage,
      reviews,
    } = this.props;
    const queryEntries = Object.keys(query);

    if (queryEntries.length <= 1) {
      return (
        <Helmet>
          {reviews && reviews.length >= REVIEWS_PER_PAGE && (
            <link
              itemProp="url"
              rel="next"
              href={`https://${location.hostname}${location.pathname}?page=${currentPage + 1}`}
            />
          )}
          {currentPage > 1 && (
            <link
              itemProp="url"
              rel="prev"
              href={`https://${location.hostname}${location.pathname}?page=${currentPage - 1}`}
            />
          )}
        </Helmet>
      );
    } else {
      return <meta name="robots" content="noindex" />;
    }
  };

  render() {
    const {
      courseName,
      currentPage,
      paging,
      reviews,
      loading,
      router,
      ratingValues,
      productCompleted,
      sortByHelpfulVotes,
    } = this.props;
    const pageCount = (paging && paging.total && Math.ceil(paging.total / REVIEWS_PER_PAGE)) || 0;

    if (loading) {
      return (
        <div className="horizontal-box align-items-absolute-center p-y-2" data-e2e="reviews-loading">
          <SvgLoaderSignal size={48} />
        </div>
      );
    }

    if (!reviews) {
      return (
        <div className="horizontal-box align-items-absolute-center p-y-2">
          <p>No reviews found for this course!</p>
        </div>
      );
    }
    const {
      location: { protocol, hostname, search, pathname },
    } = router;
    let paginationUrl;
    // if a url comes in malformed, URI throws error and breaks page
    try {
      paginationUrl = new URI(pathname).setQuery(search).deleteQueryParam('page').toString();
    } catch {
      paginationUrl = new URI().setProtocol(protocol).setHost(hostname).setPath(pathname).toString();
    }

    return (
      <div className="rc-ReviewsContainer">
        {this.renderCanonicalTags()}
        <Row rootClassName="headerFilter p-y-2 border-bottom">
          <ReviewsFilters productCompleted={productCompleted} ratingValues={ratingValues} router={router} />
          <ReviewsSort sortByHelpfulVotes={sortByHelpfulVotes} router={router} />
        </Row>
        <ReviewsList
          courseName={courseName}
          userId={user.isAuthenticatedUser() ? user.get().id : undefined}
          reviews={reviews}
          totalReviews={(paging && paging.total) || 0}
          currentPage={currentPage}
        />
        {pageCount > 0 && (
          <PaginationControls
            pageCount={pageCount}
            currentPage={currentPage}
            maxPages={12}
            url={paginationUrl}
            pageQueryParam="page"
            addQueryParamToFirstPage={false}
            style={{ display: 'flex', justifyContent: 'center', marginBottom: '3rem' }}
            numberButtonStyle={styles.numberButtonStyle}
            arrowButtonStyle={styles.arrowButtonStyle}
            disabledArrowButtonStyle={styles.disabledArrowButtonStyle}
          />
        )}
      </div>
    );
  }
}

export const COURSE_REVIEW_QUERY = gql`
  query AllCourseReviews(
    $courseId: String!
    $limit: Int!
    $start: String!
    $ratingValues: [Int!]
    $productCompleted: Boolean
    $sortByHelpfulVotes: Boolean!
  ) {
    ProductReviewsV1Resource {
      reviews: byProduct(
        productId: $courseId
        ratingValues: $ratingValues
        limit: $limit
        start: $start
        productCompleted: $productCompleted
        sortByHelpfulVotes: $sortByHelpfulVotes
      ) {
        elements {
          ...ReviewFragment
        }
        paging {
          total
        }
      }
    }
  }
  ${ReviewFragment}
`;

export default compose<Props, InputProps>(
  connectToRouter((router) => {
    const {
      location: { query },
      params: { slug },
    } = router;

    const queryPageNum = Number(query.page);
    const currentPage = queryPageNum && queryPageNum > 1 ? queryPageNum : 1;
    const startNum = currentPage === 1 ? 0 : (currentPage - 1) * REVIEWS_PER_PAGE;
    const overQueryLimit = startNum + REVIEWS_PER_PAGE >= 10000;

    // BE will return an error if start + limit is over 10000
    const start = String(overQueryLimit ? 0 : startNum);

    if (overQueryLimit && inServerContext) {
      throw new NotFoundError();
    }

    return { currentPage, start, slug, router, queryParams: query };
  }),
  withProps(({ queryParams }: { queryParams: QueryParams }) => {
    const gqlArgs: ProductReviewsGraphqlArgs = {
      limit: REVIEWS_PER_PAGE,
      sortByHelpfulVotes: !queryParams.sort || queryParams.sort === 'helpful',
      ratingValues: queryParams.star ? parseStarQueryParam(queryParams.star) : [1, 2, 3, 4, 5],
    };

    // productCompleted needs to be fully omitted from args if we don't want to filter by that value
    if (queryParams.completers === 'true') {
      gqlArgs.productCompleted = true;
    }

    return gqlArgs;
  }),
  graphql<InputProps, AllCourseReviews, AllCourseReviewsVariables, {}>(COURSE_REVIEW_QUERY, {
    props: ({ data }) => {
      return {
        reviews:
          data &&
          data.ProductReviewsV1Resource &&
          data.ProductReviewsV1Resource.reviews &&
          data.ProductReviewsV1Resource.reviews.elements,
        paging:
          data &&
          data.ProductReviewsV1Resource &&
          data &&
          data.ProductReviewsV1Resource.reviews &&
          data &&
          data.ProductReviewsV1Resource.reviews.paging,
        loading: data && data.loading,
      };
    },
  })
)(ReviewsContainer);
